import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { faArrowDown } from '@fortawesome/pro-regular-svg-icons'
import { Button } from '@/ui'
import { mtc } from '@/styles'

interface AnimatedScrollAreaProps {
  children: ReactNode
  className?: string
  /** Sizes the content column inside the scroller, so the scroller itself can span the whole area. */
  contentClassName?: string
  onScrolledChange?: (scrolled: boolean) => void
  historyRevision?: number
  conversationId?: number | null
  latestSavedReplyId?: string | null
}

export interface AnimatedScrollAreaHandle {
  scrollToBottom: (behavior?: ScrollBehavior) => void
  captureScrollPosition: () => void
}

// Within this distance from the bottom, the jump-to-latest arrow stays hidden.
const STICK_TO_BOTTOM_THRESHOLD_PX = 80

/**
 * Scrollable container with top/bottom fades, hysteresis-based compact-header
 * detection (fires `onScrolledChange` at 56/24px with a 400ms lockout to avoid
 * GSAP-induced oscillation), and a jump-to-latest arrow when content grows
 * below the fold.
 */
export const AnimatedScrollArea = forwardRef<
  AnimatedScrollAreaHandle,
  AnimatedScrollAreaProps
>((props, ref) => {
  const {
    children,
    className,
    contentClassName,
    onScrolledChange,
    historyRevision,
    conversationId,
    latestSavedReplyId,
  } = props
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentWrapperRef = useRef<HTMLDivElement>(null)
  const positionBeforePrependRef = useRef<{
    scrollHeight: number
    scrollTop: number
  } | null>(null)
  const scrolledRef = useRef(false)
  // Lockout timestamp: GSAP's header animation shifts scrollTop, so we ignore
  // scroll events briefly after a compact-state change to prevent oscillation.
  const lastChangeAtRef = useRef(0)
  const [hasContentBelow, setHasContentBelow] = useState(false)
  const [newReplyPending, setNewReplyPending] = useState(false)
  const previousReplySourceRef = useRef({
    conversationId,
    historyRevision,
    latestSavedReplyId,
  })

  const isNearBottom = useCallback((): boolean => {
    const el = scrollContainerRef.current
    if (!el) return true
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    return distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD_PX
  }, [])

  const updateContentBelow = useCallback(() => {
    const nearBottom = isNearBottom()
    setHasContentBelow(!nearBottom)
    if (nearBottom) setNewReplyPending(false)
  }, [isNearBottom])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = scrollContainerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
    setHasContentBelow(false)
    setNewReplyPending(false)
  }, [])

  const captureScrollPosition = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    positionBeforePrependRef.current = {
      scrollHeight: el.scrollHeight,
      scrollTop: el.scrollTop,
    }
  }, [])

  useImperativeHandle(ref, () => ({ scrollToBottom, captureScrollPosition }), [
    scrollToBottom,
    captureScrollPosition,
  ])

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    updateContentBelow()
    if (!onScrolledChange) return
    // 400ms lockout absorbs the scrollTop shift from the GSAP header animation.
    if (Date.now() - lastChangeAtRef.current < 400) return
    const { scrollTop } = el
    const wasCompact = scrolledRef.current
    // Separate enter/exit thresholds (hysteresis) prevent rapid toggling.
    if (!wasCompact && scrollTop > 56) {
      scrolledRef.current = true
      lastChangeAtRef.current = Date.now()
      onScrolledChange(true)
    } else if (wasCompact && scrollTop < 24) {
      scrolledRef.current = false
      lastChangeAtRef.current = Date.now()
      onScrolledChange(false)
    }
  }, [onScrolledChange, updateContentBelow])

  useLayoutEffect(() => {
    const sc = scrollContainerRef.current
    if (sc) sc.scrollTop = sc.scrollHeight
  }, [])

  useLayoutEffect(() => {
    const sc = scrollContainerRef.current
    const before = positionBeforePrependRef.current
    if (!sc || !before) return
    sc.scrollTop = before.scrollTop + sc.scrollHeight - before.scrollHeight
    positionBeforePrependRef.current = null
  }, [historyRevision])

  useLayoutEffect(() => {
    const previous = previousReplySourceRef.current
    previousReplySourceRef.current = {
      conversationId,
      historyRevision,
      latestSavedReplyId,
    }
    if (
      previous.conversationId !== conversationId ||
      previous.historyRevision !== historyRevision ||
      !latestSavedReplyId
    ) {
      setNewReplyPending(false)
      return
    }
    if (previous.latestSavedReplyId !== latestSavedReplyId && !isNearBottom()) {
      setNewReplyPending(true)
    }
  }, [conversationId, historyRevision, latestSavedReplyId, isNearBottom])

  // Content growth only toggles the arrow, so a reply growing mid-read never
  // pulls the view down. Scrolling follows a user send or an arrow tap.
  useEffect(() => {
    const content = contentWrapperRef.current
    const sc = scrollContainerRef.current
    if (!content || !sc) return undefined
    const ro = new ResizeObserver(updateContentBelow)
    ro.observe(content)
    return () => ro.disconnect()
  }, [updateContentBelow])

  return (
    <div
      className={mtc(
        'relative flex flex-col flex-1 overflow-hidden bg-neutral-0',
        className
      )}
    >
      {/* Bottom fade — absolute, outside scroll so it pins to the visible edge */}
      <div
        className="absolute inset-x-0 bottom-0 h-6 pointer-events-none z-10"
        style={{
          background:
            'linear-gradient(to top, #ffffff 0%, rgba(255,255,255,0) 100%)',
        }}
      />
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-6 pb-6 pt-0"
      >
        {/* Sticky top fade — stays pinned as content scrolls under it */}
        <div
          className="sticky top-0 -mx-6 h-6 pointer-events-none z-10 shrink-0"
          style={{
            background:
              'linear-gradient(to bottom, #ffffff 0%, rgba(255,255,255,0) 100%)',
          }}
        />
        <div
          ref={contentWrapperRef}
          className={mtc('flex flex-col gap-3', contentClassName)}
        >
          {children}
        </div>
      </div>
      {hasContentBelow && (
        <Button
          variant={newReplyPending ? 'primary' : 'secondary'}
          size="sm"
          iconLeft={faArrowDown}
          aria-label={
            newReplyPending
              ? 'New reply, jump to latest messages'
              : 'Jump to latest messages'
          }
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-full shadow-md"
        >
          {newReplyPending ? 'New reply' : 'Jump to latest'}
        </Button>
      )}
      {newReplyPending && (
        <span role="status" className="sr-only">
          New reply below
        </span>
      )}
    </div>
  )
})

AnimatedScrollArea.displayName = 'AnimatedScrollArea'
